import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  checkApi,
  type ResourceCheckAssignment,
  type SetResourceCheckAssignmentsRequest,
} from '../client';

export const assignmentKeys = {
  all: ['check', 'assignments'] as const,
  byOrganisation: (organisationId: string) =>
    ['check', 'assignments', 'org', organisationId] as const,
};

/**
 * Every template assignment in the organisation, as a flat list.
 *
 * Fetched whole rather than per row: there is at most one assignment per
 * vehicle, so the payload is small and the grid can join client-side instead
 * of firing a request per visible row.
 */
export function useResourceCheckAssignments(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<ResourceCheckAssignment[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<ResourceCheckAssignment[], Error>({
    queryKey: organisationId ? assignmentKeys.byOrganisation(organisationId) : assignmentKeys.all,
    queryFn: () => checkApi.getResourceCheckAssignments(organisationId!),
    enabled: !!organisationId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Assign a template to one or many resources, or clear them with a null
 * template. Returns the number of resources affected.
 *
 * Fails with a 400 if the template has never been published — assigning an
 * unpublished template would leave drivers with nothing to open.
 */
export function useSetResourceCheckAssignments() {
  const qc = useQueryClient();
  return useMutation<number, Error, SetResourceCheckAssignmentsRequest>({
    mutationFn: (body) => checkApi.setResourceCheckAssignments(body),
    onSuccess: (_count, body) => {
      qc.invalidateQueries({ queryKey: assignmentKeys.byOrganisation(body.organisationId) });
    },
  });
}
